import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { Box, Typography, alpha } from "@mui/material";
import { useParams } from "react-router-dom";
import ListFarmsCarousel from "../../components/Famer/ListFarmsCarousel";
import MessageInput from "../../components/Famer/SpeechToText";
import CustomAppBar from "../../components/AppBar";
import ChatMessages from "../../components/Famer/ChatMessage";
import { getRoleFromLocal } from "../../globals/authentication";
import { useTranslation } from "react-i18next";
import {
  fetchMessagesFromAPI,
  sendMessageToConversationAPI,
} from "../../api/chat";
import { isNotNullOrEmptyArray } from "../../globals/utils";

const ChatInterface = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFarmId, setSelectedFarmId] = useState(null);
  const [selectedCropId, setSelectedCropId] = useState(null);
  const messagesEndRef = useRef(null);

  // Ensure we always pass string content
  const safeContent = (content) => {
    if (typeof content === "string") return content;
    if (typeof content === "object") return JSON.stringify(content);
    return String(content);
  };

  const cleanInitialMessage = (msg, index) => {
    if (index === 0 && typeof msg.content === "string") {
      const cutoff = msg.content.indexOf("<!--Initial data");
      if (cutoff !== -1) msg.content = msg.content.slice(0, cutoff).trim();
    }
    return msg;
  };

  const fetchMessages = async () => {
    try {
      const data = await fetchMessagesFromAPI(id);
      const formatted = data
        .map((msg) => ({
          role: msg.role,
          content: safeContent(msg.content),
        }))
        .map(cleanInitialMessage);
      setMessages(formatted);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      scrollToBottom();
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    const tempMessage = { role: "user", content: newMessage };
    setMessages((prev) => [...prev, tempMessage]);
    setNewMessage("");
    setIsLoading(true);

    try {
      const payload = {
        conversation_id: id,
        message: newMessage,
        ...(messages.length === 0 && {
          ...(selectedFarmId && { farm: selectedFarmId }),
          ...(selectedCropId && { crop: selectedCropId }),
        }),
      };

      const reply = await sendMessageToConversationAPI(id, payload);
      if (reply) {
        const data = Array.isArray(reply) ? reply : [];
        const formatted = data
          .map((msg) => ({
            role: msg.role,
            content: safeContent(msg.content),
          }))
          .map(cleanInitialMessage);
        setMessages(formatted);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => prev.filter((m) => m !== tempMessage));
    } finally {
      setIsLoading(false);
      scrollToBottom();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    fetchMessages();
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const [role, setRole] = useState("");
  useEffect(() => {
    const userRole = getRoleFromLocal();
    setRole(userRole);
  }, []);

  return (
    <div>
      <Helmet>
        <title>Farmapp Support - Farmapp</title>
      </Helmet>
      <CustomAppBar />
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          background: "linear-gradient(135deg, #f4f4f4 0%, #e9e9e9 100%)",
          overflow: "hidden",
        }}
      >
        {/* Chat Header */}
        <Box
          sx={{
            padding: 2,
            backgroundColor: alpha("#ffffff", 0.6),
            backdropFilter: "blur(10px)",
            boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="text.primary">
            {t("Farmapp Support")}
          </Typography>
        </Box>

        {role === "FARMER" && !isNotNullOrEmptyArray(messages) && (
          <ListFarmsCarousel
            setSelectedFarmId={setSelectedFarmId}
            selectedFarmId={selectedFarmId}
            selectedCropId={selectedCropId}
            setSelectedCropId={setSelectedCropId}
          />
        )}

        {/* Message Area */}
        <ChatMessages
          messages={messages}
          isLoading={isLoading}
          messagesEndRef={messagesEndRef}
        />

        {/* Message Input Area */}
        <MessageInput
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          sendMessage={sendMessage}
          isLoading={isLoading}
        />
      </Box>
    </div>
  );
};

export default ChatInterface;
